import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import defineOneReachColumns from 'pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/OneReach/utils/defineOneReachColumns';
import { OneReachDataGrid } from 'pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/OneReach/styles';
import useCalculateOneReach from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useCalculateOneReach';
import convertDecimalToPercentValue from 'pages/calculator/MediaPlanning/Plan/Countries/utils/convertDecimalToPercentValue';
import { Touchpoint } from '../../../../../../../api/services/mediaPlanning/touchpoints';
import { useError } from '../../../../common/Errors/ErrorContext/ErrorContext';

export interface OneReachProps {
  availableTouchpoints: Touchpoint[];
}

function OneReach({ availableTouchpoints }: OneReachProps) {
  const { t } = useTranslation();
  const hasMetaAwareness = availableTouchpoints.some(
    (touchpoint) =>
      touchpoint.TouchpointName === 'META' &&
      touchpoint.touchpointFunnel.toLowerCase().includes('awareness')
  );
  const { showError } = useError();
  const {
    data: oneReachData,
    calculateOneReach,
    isLoading,
    isError,
  } = useCalculateOneReach();

  useEffect(() => {
    if (hasMetaAwareness) {
      calculateOneReach();
    } else {
      showError(t('mediaPlanning.errors.oneReachMetaAwarenessMissing'), 'info');
    }
  }, [calculateOneReach, hasMetaAwareness]);

  const oneReachResults = oneReachData?.results || [];
  const finalOneReachValue = oneReachData?.finalOneReachValue || 0;

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          p: '32px',
        }}
      >
        <Typography color="error">
          {t('mediaPlanning.errors.oneReachFetchingError')}
        </Typography>
      </Box>
    );
  }

  if (!hasMetaAwareness) {
    return (
      <Box
        sx={{
          display: 'flex',
          p: '32px',
        }}
      >
        <Typography color="error">
          {t('mediaPlanning.errors.oneReachNotCalculated')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ pt: '17px', pb: '56px', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: '24px' }}>
        <Typography textTransform="uppercase" fontWeight={700} fontSize={18}>
          {t('mediaPlanning.campaignOneReach')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: '16px',
            border: '1px solid',
            borderColor: 'borderDark.main',
            borderRadius: '5px',
            backgroundColor: 'tableBackground.main',
            minWidth: '110px',
            ml: '24px',
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {`${convertDecimalToPercentValue(finalOneReachValue)}%`}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="lorealPurple.main"
        textTransform="uppercase"
        fontWeight={600}
        sx={{ px: '24px', py: '40px' }}
      >
        {t('mediaPlanning.alternativeScenarios')}
      </Typography>

      <Box
        sx={{
          width: '100%',
          height: '522px',
          mb: '24px',
        }}
      >
        <OneReachDataGrid
          rows={oneReachResults.map((row, index) => ({
            ...row,
            rowIndex: index,
          }))}
          columns={defineOneReachColumns(oneReachResults)}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={(row) => `${row.touchpointName}_${row.rowIndex}`}
          getRowClassName={(params) => {
            const uniqueTouchpoints = Array.from(
              new Set(oneReachResults.map((row) => row.touchpointName))
            );

            const touchpointGroupIndex = uniqueTouchpoints.indexOf(
              params.row.touchpointName
            );

            return touchpointGroupIndex % 2 !== 0 ? 'highlighted' : '';
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'touchpoint', sort: 'asc' }],
            },
          }}
          sx={{
            '& .MuiDataGrid-row.highlighted': {
              backgroundColor: 'tableBackground.main',
            },
          }}
          loading={isLoading}
        />
      </Box>
    </Box>
  );
}

export default OneReach;
